<template>
  <div v-if="$apollo.loading">
    <spinner />
  </div>
  <div v-else class="mobile-button-container">
    <div class="title-screen">
      <span>Users</span>
    </div>
    <div class="mobile-button">
      <main-button color="primary" @click="openCreateModal()">
        <span class="add-symbol">+</span>Add User
      </main-button>
    </div>
    <v-list two-line class="transparent-list">
      <template v-for="invite in invites">
        <v-list-tile :key="`i-${invite.id}`" avatar>
          <v-list-tile-avatar>
            <v-icon>account_circle</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>{{ invite.email }}</v-list-tile-title>
            <v-list-tile-sub-title>{{ invite.organizationRole }}</v-list-tile-sub-title>
          </v-list-tile-content>
          <v-list-tile-action>
            <div
              v-if="!invite.expired"
              class="invite-right-part-container invite-sent-right-part-container"
              @click="openSentInviteInfoModal(invite)"
            >
              <img class="invite-icon" src="/assets/invite-sent-icon.svg" alt="Icon" />
              <div class="invite-text invite-sent-text">INVITE SENT</div>
            </div>
            <div
              v-else
              class="invite-right-part-container invite-expired-right-part-container"
              @click="openExpiredInviteModal(invite)"
            >
              <img class="invite-icon" src="/assets/error-icon.svg" alt="Icon" />
              <div class="invite-text invite-expired-text">EXPIRED</div>
            </div>
          </v-list-tile-action>
        </v-list-tile>
        <v-divider :key="`divider1-${invite.id}`" />
      </template>
      <template v-for="(user, i) in users">
        <v-list-tile :key="`u-${user.id}`" avatar>
          <v-list-tile-avatar>
            <v-icon>account_circle</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>{{ user.firstName }} {{ user.lastName }}</v-list-tile-title>
            <v-list-tile-sub-title>{{ user.organizationRole }}</v-list-tile-sub-title>
          </v-list-tile-content>
          <v-list-tile-action v-if="me && user.id !== me.id">
            <v-btn flat class="edit-button" @click="openEditUserModal(user)">
              <v-icon color="#0277BD">edit</v-icon>
              <p class="edit-user-text">EDIT USER</p>
            </v-btn>
          </v-list-tile-action>
        </v-list-tile>
        <v-divider v-if="i + 1 < users.length" :key="`divider2-${user.id}`" />
      </template>
    </v-list>
    <organization-user-modal
      v-if="isInviteModalVisible"
      :initial-values="form"
      :vs="$v"
      type="CREATE"
      @submit="sendUserInvite()"
      @closeModal="closeModal()"
    />
    <organization-user-modal
      v-if="isUpdateUserModalVisible"
      :initial-values="form"
      :vs="$v"
      type="UPDATE"
      @submit="updateUserRole()"
      @delete="deleteUser()"
      @closeModal="closeModal()"
    />
    <organization-user-modal
      v-if="isInviteSentModalVisible"
      :initial-values="form"
      :vs="$v"
      type="SENT"
      @submit="resendInvite()"
      @delete="deleteInvite()"
      @closeModal="closeModal()"
    />
    <organization-user-modal
      v-if="isInviteExpiredModalVisible"
      :initial-values="form"
      :vs="$v"
      type="EXPIRED"
      @submit="resendInvite()"
      @delete="deleteInvite()"
      @closeModal="closeModal()"
    />
    <simple-snackbar v-bind.sync="snackbar" />
  </div>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators'
import { omit } from 'lodash'

import { GET_ORGANIZATION_USERS_AND_INVITES } from '../../../graphql/organization.graphql'
import {
  UPDATE_USER_ORGANIZATION_ROLE,
  DELETE_USER_ORGANIZATION,
} from '../../../graphql/userOrganization.graphql'
import { GET_LOGGED_USER } from '../../../graphql/user.graphql'
import {
  INVITE_USER_TO_ORGANIZATION,
  DELETE_INVITE,
  RESEND_INVITE,
} from '../../../graphql/invite.graphql'

import organizationUserModal from './organization-user-modal.vue'
import simpleSnackbar from '../../shared/snackbars/simple-snackbar.vue'
import spinner from '../../shared/spinner.vue'
import { submitGuard } from '../../../utils'

export default {
  name: 'organization-users',
  components: { organizationUserModal, simpleSnackbar, spinner },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  data() {
    return {
      me: null,
      users: [],
      invites: [],
      form: {},
      snackbar: {},
      isInviteModalVisible: false,
      isUpdateUserModalVisible: false,
      isInviteSentModalVisible: false,
      isInviteExpiredModalVisible: false,
    }
  },
  validations() {
    return {
      form: {
        email: { required, email },
        role: { required },
      },
    }
  },
  methods: {
    openCreateModal() {
      this.isInviteModalVisible = true
    },
    openEditUserModal(user) {
      this.form = { ...omit(user, 'organizationRole'), role: user.organizationRole }
      this.isUpdateUserModalVisible = true
    },
    openSentInviteInfoModal(invite) {
      this.form = { email: invite.email, role: invite.organizationRole, id: invite.id }
      this.isInviteSentModalVisible = true
    },
    openExpiredInviteModal(invite) {
      this.form = { email: invite.email, role: invite.organizationRole, id: invite.id }
      this.isInviteExpiredModalVisible = true
    },
    async closeModal() {
      this.isInviteModalVisible = false
      this.isUpdateUserModalVisible = false
      this.isInviteSentModalVisible = false
      this.isInviteExpiredModalVisible = false
      this.form = {}
      this.$v.$reset()
    },
    async sendUserInvite() {
      this.$v.form.$touch()

      if (this.$v.form.$invalid === true) {
        this.snackbar = {
          open: true,
          messageProp: 'Fill in the required information.',
          color: 'error',
        }
      } else {
        await submitGuard(this, async () => {
          const { errors } = await this.$apollo.mutate({
            mutation: INVITE_USER_TO_ORGANIZATION,
            variables: { organizationSlug: this.organizationSlug, inviteData: this.form },
            refetchQueries: [
              {
                query: GET_ORGANIZATION_USERS_AND_INVITES,
                variables: { organizationSlug: this.organizationSlug },
              },
            ],
          })

          if (errors) {
            this.snackbar = {
              open: true,
              messageProp: errors[0].message,
              color: 'error',
            }
          } else {
            this.snackbar = {
              open: true,
              messageProp: 'Successfully sent invite!',
              color: 'success',
            }
            this.closeModal()
          }
        })
      }
    },
    async updateUserRole() {
      this.$v.form.$touch()

      if (this.$v.form.$invalid === true) {
        this.snackbar = {
          open: true,
          messageProp: 'Fill in the required information.',
          color: 'error',
        }
      } else {
        await submitGuard(this, async () => {
          const { errors } = await this.$apollo.mutate({
            mutation: UPDATE_USER_ORGANIZATION_ROLE,
            variables: {
              organizationSlug: this.organizationSlug,
              email: this.form.email,
              role: this.form.role,
            },
            refetchQueries: [
              {
                query: GET_ORGANIZATION_USERS_AND_INVITES,
                variables: { organizationSlug: this.organizationSlug },
              },
            ],
          })

          if (errors) {
            this.snackbar = {
              open: true,
              messageProp: errors[0].message,
              color: 'error',
            }
          } else {
            this.snackbar = {
              open: true,
              messageProp: 'Successfully updated user!',
              color: 'success',
            }
            this.closeModal()
          }
        })
      }
    },
    async deleteUser() {
      const { errors } = await this.$apollo.mutate({
        mutation: DELETE_USER_ORGANIZATION,
        variables: {
          organizationSlug: this.organizationSlug,
          email: this.form.email,
        },
        refetchQueries: [
          {
            query: GET_ORGANIZATION_USERS_AND_INVITES,
            variables: { organizationSlug: this.organizationSlug },
          },
        ],
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: errors[0].message,
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: 'Successfully deleted user!',
          color: 'success',
        }
        this.closeModal()
      }
    },
    async resendInvite() {
      const { errors } = await this.$apollo.mutate({
        mutation: RESEND_INVITE,
        variables: {
          organizationSlug: this.organizationSlug,
          inviteId: this.form.id,
        },
        refetchQueries: [
          {
            query: GET_ORGANIZATION_USERS_AND_INVITES,
            variables: { organizationSlug: this.organizationSlug },
          },
        ],
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: errors[0].message,
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: 'Successfully resent invite!',
          color: 'success',
        }
        this.closeModal()
      }
    },
    async deleteInvite() {
      const { errors } = await this.$apollo.mutate({
        mutation: DELETE_INVITE,
        variables: {
          organizationSlug: this.organizationSlug,
          inviteId: this.form.id,
        },
        refetchQueries: [
          {
            query: GET_ORGANIZATION_USERS_AND_INVITES,
            variables: { organizationSlug: this.organizationSlug },
          },
        ],
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: errors[0].message,
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: 'Successfully deleted invite!',
          color: 'success',
        }
        this.closeModal()
      }
    },
  },
  apollo: {
    getOrganizationUsers: {
      query: GET_ORGANIZATION_USERS_AND_INVITES,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      result({ data, loading }) {
        if (!loading && data) {
          this.users = data.getOrganizationUsers
          this.invites = data.getOrganizationInvites
        }
      },
    },
    me: {
      query: GET_LOGGED_USER,
      result({ data, loading }) {
        if (!loading && data) {
          this.me = data.me
        }
      },
    },
  },
}
</script>

<style scoped>
.mobile-button-container {
  max-width: 48rem;
}
.add-symbol {
  font-size: 1.875rem;
  margin-right: 0.8125rem;
  font-weight: 200;
}
@media screen and (max-width: 800px) {
  .add-symbol {
    display: none;
  }
}
.edit-user-text {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  text-transform: uppercase;
  margin-bottom: 0 !important;
  margin-left: 0.75rem;
  color: #0277bd;
  font-feature-settings: 'pnum' on, 'lnum' on;
}
.edit-button {
  background: transparent;
  width: 150px;
  border-radius: 8px;
}
.invite-right-part-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  padding: 8px 12px;
}
.invite-right-part-container:hover {
  cursor: pointer;
}
.invite-sent-right-part-container:hover {
  background-color: #55a35d20;
}
.invite-expired-right-part-container:hover {
  background-color: #db384c20;
}
.invite-text {
  font-family: Raleway;
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 120%;
  text-align: center;
  letter-spacing: -0.015em;
  text-transform: uppercase;
  font-feature-settings: 'pnum' on, 'lnum' on;
}
.invite-sent-text {
  color: #55a35d;
}
.invite-expired-text {
  color: #db384c;
}
</style>
